import { useMemo } from 'react';
import { type ComponentProps } from 'react';

import { getCloudinaryUrl, PRODUCT_CARD_IMAGE_CONFIG } from '@garnish/media';
import { type Asset } from 'contentful';

import { type ProductDetailsCardCarousel } from '@order/features-product-details';

// ─────────────────────────────────────────────────────────────────────────────

export const useProductDetailsCarouselItems = (params: UseProductDetailsCarouselItemsParams) => {
  const { carousel, productAssetUrl, productName } = params;

  const carouselItems = useMemo<ProductDetailsCardCarouselItems>(() => {
    if (!carousel || carousel.length === 0) return [];

    const baseImageItem: ProductDetailsCardCarouselItems[number] = {
      type: 'image',
      source: getCloudinaryUrl(productAssetUrl, PRODUCT_CARD_IMAGE_CONFIG),
      alt: productName,
      contentFit: 'contain',
    };
    const otherItems = carousel.map(transformAssetToCarouselItem);

    return [baseImageItem, ...otherItems];
  }, [carousel, productAssetUrl, productName]);

  // ───────────────────────────────────────────────────────────────────────────

  return {
    carouselItems,
  };
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function transformAssetToCarouselItem(asset: {
  fields: { imageVideo: Asset; staticImage?: Asset };
}): ProductDetailsCardCarouselItems[number] {
  const media = asset.fields.imageVideo;
  const isVideo = media.fields.file.contentType.startsWith('video');
  const source = media.fields.file.url;
  const staticSource = asset.fields.staticImage?.fields?.file?.url;
  const alt = media.fields.title ?? media.fields.description;
  const type = isVideo ? 'video' : 'image';

  return { type, source, staticSource, alt };
}

// ─── Types ───────────────────────────────────────────────────────────────────

type ProductDetailsCardCarouselItems = ComponentProps<typeof ProductDetailsCardCarousel>['items'];

type UseProductDetailsCarouselItemsParams = {
  carousel: { fields: { imageVideo: Asset; staticImage?: Asset } }[] | undefined;
  productAssetUrl: string;
  productName: string;
};
